import { motion } from 'framer-motion';
import {
  EnvelopeIcon,
  MapPinIcon,
  CalendarIcon,
  ArrowTopRightOnSquareIcon,
  DocumentChartBarIcon,
  ArrowRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const Contact = () => {
  return (
    <section id="contact" className="py-24 relative overflow-hidden bg-gradient-to-b from-white via-[#FF7066]/5 to-white">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.03]" />
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(255, 112, 102, 0.03), transparent 70%)',
        }}
      />
      
      {/* Floating Orbs */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        className="absolute top-20 right-20 w-64 h-64 bg-[#FF7066]/10 rounded-full blur-3xl"
      />
      
      <div className="container relative z-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-[#FF7066]/10 to-secondary/10 text-[#FF7066] text-sm font-medium border border-[#FF7066]/10 mb-6"
            >
              Get In Touch
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-primary mb-6"
            >
              Let's <span className="bg-gradient-to-r from-[#FF7066] to-secondary bg-clip-text text-transparent">Connect</span>
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-xl text-secondary mb-8"
            >
              Ready to transform your business with AI? We're here to help you get started.
            </motion.p>
          </div>

          {/* Bento Box Layout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto"
          >
            {/* Large Card: Book a Meeting - REDESIGNED */}
            <div className="md:col-span-2 bg-white/50 backdrop-blur-sm rounded-3xl border border-[#FF7066]/10 p-8 hover:shadow-xl hover:shadow-[#FF7066]/5 transition-all duration-300 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-[#FF7066]/10 to-accent/5 rounded-full -mr-20 -mt-20 blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-primary/10 to-[#FF7066]/5 rounded-full -ml-10 -mb-10 blur-xl"></div>
              
              <div className="relative z-10">
                <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/70 text-[#FF7066] text-sm font-medium mb-4">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Free 30-Minute Consultation
                </div>
                
                <h3 className="text-2xl font-bold text-primary mb-4">
                  Book Your <span className="text-[#FF7066]">Strategy Session</span>
                </h3>
                
                <div className="flex flex-col md:flex-row gap-8 items-start md:items-center">
                  <div className="flex-grow">
                    <p className="text-secondary mb-4">
                      Choose a convenient time for a discussion about your business needs and how our AI solutions can help you achieve your goals.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                      {[
                        "Free 30-minute discovery call", 
                        "Understand your needs", 
                        "No obligation", 
                        "Custom solution proposals"
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <CheckCircleIcon className="w-4 h-4 text-[#FF7066]" />
                          <span className="text-xs text-secondary">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <a
                      href="https://calendly.com/amrit-betterbyai/30min" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#FF7066] text-white hover:bg-[#FF8A82] transition-colors group whitespace-nowrap"
                    >
                      Book Your Session
                      <ArrowTopRightOnSquareIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                    <p className="text-xs text-secondary/70 mt-2 text-center">No preparation needed</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Email Us Card */}
            <div className="bg-white/50 backdrop-blur-sm rounded-3xl border border-[#FF7066]/10 p-6 hover:shadow-xl hover:shadow-[#FF7066]/5 transition-all duration-300 flex flex-col justify-between">
              <div>
                <div className="p-3 rounded-lg bg-[#FF7066]/10 inline-block mb-4">
                  <EnvelopeIcon className="w-5 h-5 text-[#FF7066]" />
                </div>
                <h4 className="font-medium text-primary mb-2">Send us an Email</h4>
                <p className="text-secondary/80 mb-4">Drop us a line and we'll get back to you as soon as possible.</p>
              </div>
              <a 
                href="mailto:amrit@betterbyai.com" 
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg border border-[#FF7066] text-[#FF7066] hover:bg-[#FF7066]/10 transition-colors"
              >
                Email Us <ArrowTopRightOnSquareIcon className="w-4 h-4" />
              </a>
            </div>
            
            {/* AI Audit Block - Featured (unchanged) */}
            <div className="md:col-span-2 bg-gradient-to-br from-[#FF7066]/10 to-accent/10 backdrop-blur-sm rounded-3xl border border-[#FF7066]/20 p-8 hover:shadow-xl hover:shadow-[#FF7066]/5 transition-all duration-300 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-[#FF7066]/10 to-accent/5 rounded-full -mr-20 -mt-20 blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-primary/10 to-[#FF7066]/5 rounded-full -ml-10 -mb-10 blur-xl"></div>
              
              <div className="relative z-10">
                <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/70 text-[#FF7066] text-sm font-medium mb-4">
                  <DocumentChartBarIcon className="w-4 h-4 mr-2" />
                  Free AI Business Analysis
                </div>
                
                <h3 className="text-2xl font-bold text-primary mb-4">
                  Discover Your <span className="text-[#FF7066]">Untapped AI Potential</span>
                </h3>
                
                <div className="flex flex-col md:flex-row gap-8 items-start md:items-center">
                  <div className="flex-grow">
                    <p className="text-secondary mb-4">
                      Our bespoke AI audit reveals tailored opportunities to boost efficiency and drive growth with artificial intelligence.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                      {["Identify automation opportunities", "Uncover revenue potential", "Strategic implementation roadmap", "Competitive analysis"].map((item, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <CheckCircleIcon className="w-4 h-4 text-[#FF7066]" />
                          <span className="text-xs text-secondary">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <Link 
                      to="/free-ai-audit" 
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-white/80 border border-[#FF7066]/20 text-[#FF7066] hover:bg-[#FF7066]/10 transition-colors group whitespace-nowrap"
                      onClick={() => {
                        window.scrollTo(0, 0);
                      }}
                    >
                      Claim Your Free Audit 
                      <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <p className="text-xs text-secondary/70 mt-2 text-center">Takes only 2 minutes</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Location Card (unchanged) */}
            <div className="bg-gradient-to-br from-[#FF7066]/5 to-secondary/5 p-6 rounded-2xl">
              <div className="p-2 rounded-lg bg-white/50 inline-block mb-4">
                <MapPinIcon className="w-5 h-5 text-[#FF7066]" />
              </div>
              <h4 className="font-medium text-primary mb-3">Our Location</h4>
              <p className="text-secondary mb-1">London, United Kingdom</p>
              <p className="text-secondary/80 text-sm">We work with clients globally through virtual meetings.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;